import { HttpStatusCode } from "axios";
import clsx from "clsx";
import React, { useEffect, useState } from "react";

import { CATEGORY_ALIAS } from "../../../types/talk";

import { MANAGER_ROLE } from "../../../consts/member";

import { findCategoryByAlias } from "../../../libs/category";

import {
  getTalkCategories,
  TalkCategory,
} from "../../../services/talkServices";
import { useSandiTalk } from "../../../contexts/boardContext";
import { useUserContext } from "../../../contexts/userContext";

import useSimplePopup from "../../../hooks/useSimplePopup";

import CommonPopup from "..";
import HorizontalRule from "../../Rule/Horizontal";

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
};

// eslint-disable-next-line no-empty-pattern
export default function CategoryListPopup(props: Props) {
  const [categoryList, setCategoryList] = useState<TalkCategory[]>([]);
  const [talk, { updateCategory }] = useSandiTalk();
  const [user] = useUserContext();
  const [simplePopupComponent, { openError }] = useSimplePopup({});

  const handleGotoCategory = (alias: string) => {
    const categoryInfo = findCategoryByAlias(talk.categoryList ?? [], alias);
    updateCategory({ categoryInfo });
    props?.onClose?.();
  };

  const handleGotoLargeCategory = (largeCategory: TalkCategory) => {
    if (largeCategory.lifeCategoryList) {
      const middleCategory = largeCategory.lifeCategoryList[0];
      if (middleCategory) {
        return handleGotoCategory(
          middleCategory.alias ?? CATEGORY_ALIAS.TALK_ALL
        );
      } else {
        return handleGotoCategory(
          largeCategory.alias ?? CATEGORY_ALIAS.TALK_ALL
        );
      }
    } else {
      return handleGotoCategory(largeCategory.alias ?? CATEGORY_ALIAS.TALK_ALL);
    }
  };

  const defaultCategory: (TalkCategory & { onClick?: () => void })[] = [
    {
      name: "전체글 보기",
      icon: <AllPostIcon />,
      onClick: () => {
        handleGotoCategory(CATEGORY_ALIAS.TALK_ALL);
      },
    },
    {
      name: "인기글 보기",
      icon: <PopularPostIcon />,
      onClick: () => {
        handleGotoCategory(CATEGORY_ALIAS.TALK_POPULAR);
      },
    },
    {
      name: "샌디 이벤트/공지",
      icon: <EventPostIcon />,
      onClick: () => {
        handleGotoCategory(CATEGORY_ALIAS.TALK_SANDI_EVENT);
      },
    },
  ];

  const getCategory = async () => {
    try {
      const result = await getTalkCategories({
        buildingId: talk.buildingId,
      });
      if (result.status === HttpStatusCode.Ok) {
        setCategoryList(result.data);
      } else {
        throw new Error("게시판 카테고리 정보 조회 실패");
      }
    } catch (e: any) {
      openError(e, "게시판 카테고리 정보 조회 실패");
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    getCategory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talk.buildingId, user.isLoading]);

  return (
    <div data-testid="category-list-popup-wrapper">
      <CommonPopup
        isOpen={props.isOpen}
        onClose={props.onClose}
        title="샌디톡 카테고리"
      >
        <div className="flex flex-col gap-7 pb-5">
          <div className="flex flex-col gap-7">
            {defaultCategory?.map((item, index) => (
              <div
                className="flex items-center gap-2.5 text-base font-semibold"
                key={index}
                onClick={item.onClick}
                data-testid="default-category-item"
              >
                {item.icon} {item.name}
              </div>
            ))}
          </div>
          {categoryList
            .filter((item) => item.alias !== CATEGORY_ALIAS.SANDI_INFO)
            .map((largeCategory) => (
              <div
                key={largeCategory.lifeCategoryId}
                data-testid="large-category-item"
              >
                <div className="flex justify-between items-center">
                  <div className=" text-xl font-semibold">
                    {largeCategory.name}
                  </div>
                  <div
                    onClick={() =>
                      handleGotoLargeCategory(
                        talk.categoryList?.find(
                          (item) =>
                            item.lifeCategoryId === largeCategory.lifeCategoryId
                        ) ?? largeCategory
                      )
                    }
                  >
                    <TalkViewAllPlusIcon />
                  </div>
                </div>
                <HorizontalRule className="bg-black" thickness={2} />
                <div className="flex flex-col gap-5">
                  {largeCategory.lifeCategoryList?.map((middleCategory) => {
                    // 매니저 권한만 읽기 가능할 경우, 유저 롤이 있으면 노출 제외
                    if (
                      middleCategory?.managerOnlyRole?.includes("R") &&
                      !user?.role?.includes(MANAGER_ROLE.MANAGER)
                    ) {
                      return null;
                    }

                    return (
                      <div
                        className="flex justify-between"
                        key={middleCategory.lifeCategoryId}
                        data-testid="middle-category-item"
                      >
                        <div
                          onClick={() => {
                            updateCategory({
                              categoryInfo: { largeCategory, middleCategory },
                            });
                            props?.onClose?.();
                          }}
                          className="w-full"
                        >
                          {middleCategory.name}
                        </div>
                        {/* <TalkCategoryAlertSwitch /> */}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </CommonPopup>
      {simplePopupComponent}
    </div>
  );
}

export type TalkCategoryAlertSwitchProps = {
  checked?: boolean;
  onClick?: () => void;
};

export const TalkCategoryAlertSwitch = ({
  checked,
  onClick,
}: TalkCategoryAlertSwitchProps) => {
  const [checkedState, setCheckedState] = useState<boolean>(checked ?? false);

  return (
    <div
      className={clsx([
        "flex gap-2",
        {
          "text-brand-primary-magenta": checkedState === true,
          "text-brand-neutral-30": checkedState !== true,
        },
      ])}
      // TODO: api 연동으로 교체 필요. 유저관련 백엔드 개선 필요
      // onClick={onClick}
      onClick={() => setCheckedState(!checkedState)}
    >
      {checkedState === true ? "ON" : "OFF"}
      {checkedState === true ? <CategoryAlertOn /> : <CategoryAlertOff />}
    </div>
  );
};

export const CategoryAlertOff = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.17578 16.0722H20.8225M5.21194 16.0722V9.28501C5.21194 5.53654 8.25068 2.4978 11.9991 2.4978C15.7476 2.4978 18.7863 5.53654 18.7863 9.28501V16.0722M9.28426 18.1084V18.7871C9.28426 20.2865 10.4998 21.502 11.9991 21.502C13.4985 21.502 14.714 20.2865 14.714 18.7871V18.1084"
      stroke="black"
      strokeWidth="1.75"
    />
  </svg>
);

export const CategoryAlertOn = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.7863 16.0722H5.21194V9.28501C5.21194 5.53654 8.25068 2.4978 11.9991 2.4978C15.7476 2.4978 18.7863 5.53654 18.7863 9.28501V16.0722Z"
      fill="url(#paint0_linear_1163_24682)"
    />
    <path
      d="M3.17578 16.0722H20.8225M5.21194 16.0722V9.28501C5.21194 5.53654 8.25068 2.4978 11.9991 2.4978C15.7476 2.4978 18.7863 5.53654 18.7863 9.28501V16.0722M9.28426 18.1084V18.7871C9.28426 20.2865 10.4998 21.502 11.9991 21.502C13.4985 21.502 14.714 20.2865 14.714 18.7871V18.1084"
      stroke="black"
      strokeWidth="1.75"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1163_24682"
        x1="20.8225"
        y1="2.4978"
        x2="1.87254"
        y2="7.02666"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const TalkViewAllPlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99899 2.91507V13.0849"
      stroke="#7A7A7A"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M2.91406 8H13.0839"
      stroke="#7A7A7A"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const EventPostIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 0.5C11.3137 0.5 14 3.18641 14 6.50011C14 7.21369 14 7.8537 14 8.32222C14 10.4556 16 11.1667 16 11.1667L0 11.1667C0 11.1667 2 10.4556 2 8.32222C2 7.8537 2 7.21369 2 6.50011C2 3.18641 4.68629 0.5 8 0.5Z"
      fill="#CFCFCF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 12.5C4 14.7091 5.79086 16.5 8 16.5C10.2091 16.5 12 14.7091 12 12.5"
      fill="#CFCFCF"
    />
  </svg>
);

export const NotificationPostIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 0.5C11.3137 0.5 14 3.18641 14 6.50011C14 7.21369 14 7.8537 14 8.32222C14 10.4556 16 11.1667 16 11.1667L0 11.1667C0 11.1667 2 10.4556 2 8.32222C2 7.8537 2 7.21369 2 6.50011C2 3.18641 4.68629 0.5 8 0.5Z"
      fill="#CFCFCF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 12.5C4 14.7091 5.79086 16.5 8 16.5C10.2091 16.5 12 14.7091 12 12.5"
      fill="#CFCFCF"
    />
  </svg>
);

export const PopularPostIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.37016 6.38271C5.05946 6.7452 4.88867 7.20687 4.88867 7.68429V13.5C4.88867 14.6046 5.7841 15.5 6.88867 15.5H11.7751C12.7416 15.5 13.5699 14.8088 13.7428 13.8578L14.5711 9.30222C14.7943 8.07453 13.8512 6.94444 12.6034 6.94444H9.55534L10.8215 3.14604C10.9875 2.64784 10.762 2.10333 10.2923 1.86847C9.85918 1.65192 9.33426 1.75792 9.01913 2.12558L5.37016 6.38271Z"
      fill="#CFCFCF"
      stroke="#CFCFCF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.66667 14.5V7.5V14.5Z"
      fill="#CFCFCF"
    />
    <path
      d="M2.66667 14.5V7.5"
      stroke="#CFCFCF"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const AllPostIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="5.66602"
      y="5.16663"
      width="8"
      height="9.33333"
      fill="#CFCFCF"
      stroke="#CFCFCF"
      strokeLinejoin="round"
    />
    <path
      d="M3 11.8333C3 11.8333 3 3.16667 3 2.5H11"
      stroke="#CFCFCF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
